import React, { useState, useCallback } from 'react';
import { Plus, X, Library } from 'lucide-react';
import { toast } from 'react-toastify';
import { useSectionsLessons } from '../hooks/useSectionsLessons';
import type { CourseSectionsService, SectionWithLessons, Lesson } from '../types';
import SectionCard from './SectionCard';
import AddSectionForm from './AddSectionForm';
import ContentOverview from './ContentOverview';
import LessonFormModal from './LessonFormModal';
import LibraryPickerModal from './LibraryPickerModal';

interface SectionsLessonsTabProps {
  courseId?: number;
  onUpdate?: (sections: SectionWithLessons[]) => void;
  service: CourseSectionsService;
}

const SectionsLessonsTab: React.FC<SectionsLessonsTabProps> = ({ courseId, onUpdate, service }) => {
  const [isAddingSection, setIsAddingSection] = useState(false);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const [lessonModalState, setLessonModalState] = useState<{
    isOpen: boolean;
    editingLesson: Lesson | null;
    defaultSectionId: number | null;
  }>({ isOpen: false, editingLesson: null, defaultSectionId: null });

  const {
    sections,
    assessments,
    isLoading,
    error,
    addSection,
    isAddingSection: isSavingSection,
    addLesson,
    isAddingLesson,
    updateLesson,
    isUpdatingLesson,
    deleteLesson,
    toggleSection,
    reorderLessons,
    reorderLessonsLocal,
  } = useSectionsLessons(courseId, service);

  // Notify parent when sections change
  React.useEffect(() => {
    if (sections.length > 0) {
      onUpdate?.(sections);
    }
  }, [sections, onUpdate]);

  const handleAddSection = useCallback(async (title: string) => {
    try {
      await addSection(title);
      setIsAddingSection(false);
      toast.success('Section added successfully');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add section');
    }
  }, [addSection]);

  const openAddLesson = useCallback((sectionId?: number) => {
    const targetSectionId = sectionId ?? sections[0]?.id ?? null;
    setLessonModalState({
      isOpen: true,
      editingLesson: null,
      defaultSectionId: targetSectionId,
    });
  }, [sections]);

  const openEditLesson = useCallback((lesson: Lesson) => {
    setLessonModalState({
      isOpen: true,
      editingLesson: lesson,
      defaultSectionId: lesson.sectionId ?? null,
    });
  }, []);

  const closeLessonModal = useCallback(() => {
    setLessonModalState({ isOpen: false, editingLesson: null, defaultSectionId: null });
  }, []);

  const handleSaveLesson = useCallback(async (lessonData: Lesson, file: File | null) => {
    try {
      if (lessonData.id) {
        await updateLesson(lessonData);
        toast.success('Lesson updated successfully');
      } else {
        await addLesson({ lessonData, file });
        toast.success('Lesson added successfully');
      }
      closeLessonModal();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save lesson');
    }
  }, [addLesson, updateLesson, closeLessonModal]);

  const handleAddFromLibrary = useCallback(async (lessons: Lesson[]) => {
    if (lessons.length === 0) return;
    let added = 0;
    for (const lessonData of lessons) {
      try {
        await addLesson({ lessonData, file: null });
        added += 1;
      } catch (err) {
        toast.error(`Failed to add "${lessonData.title}": ${err instanceof Error ? err.message : 'error'}`);
      }
    }
    if (added > 0) {
      toast.success(`Added ${added} lesson${added > 1 ? 's' : ''} from library`);
      setShowLibraryPicker(false);
    }
  }, [addLesson]);

  const handleDeleteLesson = useCallback(async (lessonId: number | string) => {
    if (!window.confirm('Are you sure you want to delete this lesson?')) return;
    try {
      await deleteLesson(Number(lessonId));
      toast.success('Lesson deleted successfully');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete lesson');
    }
  }, [deleteLesson]);

  const handleReorderLessons = useCallback(async (sectionId: number, reorderedLessons: Lesson[]) => {
    // Optimistically update the UI
    reorderLessonsLocal(sectionId, reorderedLessons);

    // Persist to backend
    const lessonOrders = reorderedLessons.map((lesson, idx) => ({
      lessonId: Number(lesson.id),
      lessonOrder: idx + 1,
    }));

    try {
      await reorderLessons({ sectionId, lessonOrders });
    } catch (err) {
      toast.error('Failed to save lesson order');
    }
  }, [reorderLessons, reorderLessonsLocal]);

  if (!courseId) {
    return (
      <div className="tab-content-wrapper">
        <div className="info-message">
          <p>Please save the basic course information first before adding sections and lessons.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-content-wrapper">
      <div className="tab-header">
        <h2>Course Sections & Lessons</h2>
        <p className="tab-subtitle">
          Organize your course content into sections and add lessons to each section.
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => {}} className="close-btn">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        {!isAddingSection && (
          <button onClick={() => setIsAddingSection(true)} className="btn-primary">
            <Plus size={18} />
            Add Section
          </button>
        )}
        {sections.length > 0 && !isAddingSection && (
          <button
            onClick={() => openAddLesson()}
            className="btn-secondary"
            style={{ backgroundColor: '#4CAF50', color: 'white', border: 'none' }}
          >
            <Plus size={18} />
            Add Lesson
          </button>
        )}
        {sections.length > 0 && !isAddingSection && service.getLibraryLessons && (
          <button
            onClick={() => setShowLibraryPicker(true)}
            className="btn-secondary"
            style={{ backgroundColor: '#fff', color: '#4CAF50', border: '1px solid #4CAF50' }}
          >
            <Library size={18} />
            Add from Library
          </button>
        )}
      </div>

      {/* Add Section Form */}
      {isAddingSection && (
        <AddSectionForm
          onSave={handleAddSection}
          onCancel={() => setIsAddingSection(false)}
          isSaving={isSavingSection}
        />
      )}

      <ContentOverview sections={sections} />

      {/* Sections List */}
      <div className="sections-list">
        {sections.length === 0 && !isLoading && (
          <div className="empty-state">
            <p>No sections added yet. Click "Add Section" to get started.</p>
          </div>
        )}

        {sections.map((section, index) => (
          <SectionCard
            key={section.id}
            section={section}
            index={index}
            onToggle={toggleSection}
            onAddLesson={openAddLesson}
            onEditLesson={openEditLesson}
            onDeleteLesson={handleDeleteLesson}
            onReorderLessons={handleReorderLessons}
          />
        ))}
      </div>

      {/* Lesson Form Modal */}
      {lessonModalState.isOpen && (
        <LessonFormModal
          sections={sections}
          assessments={assessments}
          editingLesson={lessonModalState.editingLesson}
          defaultSectionId={lessonModalState.defaultSectionId}
          isSaving={isAddingLesson || isUpdatingLesson}
          onSave={handleSaveLesson}
          onClose={closeLessonModal}
        />
      )}

      {/* Add from Library Modal */}
      {showLibraryPicker && service.getLibraryLessons && (
        <LibraryPickerModal
          sections={sections}
          defaultSectionId={sections[0]?.id ?? null}
          getLibraryLessons={service.getLibraryLessons}
          isSaving={isAddingLesson}
          onAdd={handleAddFromLibrary}
          onClose={() => setShowLibraryPicker(false)}
        />
      )}

      {isLoading && !lessonModalState.isOpen && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}
    </div>
  );
};

export default SectionsLessonsTab;
